import { useEffect, useState } from "react";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Section, Pill } from "./PortalPage";
import { fmtDate, fmtMoney, INVOICE_STATUS } from "./portalUtils";
import { downloadAuthFile, openAuthFile } from "../../lib/fileOpen";
import { ArrowSquareOut, DownloadSimple, CaretLeft, CaretRight, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
};
const monthLabel = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }); };

/* Portal Invoices page: preview the branded monthly statement before it lands in your inbox. */
export function StatementSection({ testid = "statement" }) {
  const [month, setMonth] = useState(thisMonth());
  const [stmt, setStmt] = useState(null);
  const [error, setError] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let alive = true;
    setStmt(null); setError(false);
    API.get("/client-hub/statement", { params: { month } }).then(({ data }) => alive && setStmt(data)).catch(() => alive && setError(true));
    return () => { alive = false; };
  }, [month]);

  useEffect(() => {
    let alive = true, url = null;
    setPreview(null);
    API.get("/client-hub/statement.pdf", { params: { month, format: "png" }, responseType: "blob" })
      .then(({ data }) => { if (alive) { url = URL.createObjectURL(data); setPreview(url); } })
      .catch(() => alive && setPreview("error"));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [month]);

  const pdfUrl = `/api/client-hub/statement.pdf?month=${month}`;
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "open") await openAuthFile(pdfUrl);
      else await downloadAuthFile(pdfUrl, `statement-${month}.pdf`);
    } catch { toast.error("Couldn't build the statement"); }
    finally { setBusy(""); }
  };

  const t = stmt?.totals || {};
  const atLatest = month >= thisMonth();
  const atEarliest = stmt?.earliest_month ? month <= stmt.earliest_month : false;
  const grouped = (stmt?.groups || []).length > 1;

  const action = (
    <div className="flex items-center gap-1.5">
      <div className="inline-flex items-center rounded-md border border-border bg-background" data-testid={`${testid}-month-picker`}>
        <button type="button" onClick={() => setMonth((m) => shiftMonth(m, -1))} disabled={atEarliest} className="h-8 w-8 inline-flex items-center justify-center hover:bg-muted/40 disabled:opacity-30 rounded-l-md" aria-label="Previous month" data-testid={`${testid}-prev-month`}><CaretLeft size={14} /></button>
        <input type="month" value={month} max={thisMonth()} min={stmt?.earliest_month || undefined} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-8 bg-transparent px-1 text-xs border-x border-border" data-testid={`${testid}-month`} />
        <button type="button" onClick={() => setMonth((m) => shiftMonth(m, 1))} disabled={atLatest} className="h-8 w-8 inline-flex items-center justify-center hover:bg-muted/40 disabled:opacity-30 rounded-r-md" aria-label="Next month" data-testid={`${testid}-next-month`}><CaretRight size={14} /></button>
      </div>
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => run("open")} disabled={!!busy} data-testid={`${testid}-open-pdf`}><ArrowSquareOut size={13} className="mr-1" /> {busy === "open" ? "Opening…" : "Open PDF"}</Button>
      <Button size="sm" className="h-8 text-xs" onClick={() => run("download")} disabled={!!busy} data-testid={`${testid}-download-pdf`}><DownloadSimple size={13} className="mr-1" /> {busy === "download" ? "Building…" : "Download"}</Button>
    </div>
  );

  return (
    <Section title="Monthly statement" action={action} className="mb-6" testid={`${testid}-section`}>
      <p className="text-xs text-muted-foreground -mt-1 mb-4" data-testid={`${testid}-intro`}>
        Every invoice raised to you in <span className="font-medium text-foreground">{monthLabel(month)}</span>, exactly as it appears on the statement PDF{stmt?.is_main ? " — grouped by the client each piece of work was for" : ""}.
      </p>
      <div className="grid lg:grid-cols-[1fr_280px] gap-5">
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid={`${testid}-totals`}>
            {[["Invoices", t.count ?? "—", "", "count"], ["Invoiced", fmtMoney(t.total), "", "total"], ["Paid", fmtMoney(t.paid), "text-emerald-600", "paid"], ["Outstanding", fmtMoney(t.outstanding), t.outstanding > 0 ? "text-amber-600" : "", "outstanding"]].map(([l, v, cls, k]) => (
              <div key={k} className="rounded-md border border-border bg-muted/30 px-3 py-2" data-testid={`${testid}-total-${k}`}>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{l}</p>
                <p className={`text-base font-bold font-[Chivo] ${cls}`}>{stmt ? v : "…"}</p>
              </div>
            ))}
          </div>
          {error ? <p className="text-xs text-red-600" data-testid={`${testid}-error`}>Couldn't load this month's statement.</p>
            : !stmt ? <div className="h-24 rounded-md bg-muted/40 animate-pulse" />
            : stmt.groups.length === 0 ? <p className="text-xs text-muted-foreground border border-dashed border-border rounded-md py-6 text-center" data-testid={`${testid}-empty`}>No invoices were raised in {monthLabel(month)}.</p>
            : (
              <div className="space-y-4" data-testid={`${testid}-groups`}>
                {stmt.groups.map((g) => (
                  <div key={g.client_id} data-testid={`${testid}-group-${g.client_id}`}>
                    {grouped && <p className="text-xs font-semibold mb-1.5 flex items-center justify-between"><span>{g.name} <span className="text-muted-foreground font-normal">· {g.invoices.length} invoice{g.invoices.length === 1 ? "" : "s"}</span></span><span className="font-[Chivo]">{fmtMoney(g.total)}</span></p>}
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-wider text-muted-foreground"><tr className="border-b border-border"><th className="text-left py-1.5 pr-2 font-semibold">Date</th><th className="text-left py-1.5 px-2 font-semibold">Invoice</th><th className="text-left py-1.5 px-2 font-semibold hidden sm:table-cell">Work / PO</th><th className="text-left py-1.5 px-2 font-semibold">Status</th><th className="text-right py-1.5 pl-2 font-semibold">Total</th></tr></thead>
                      <tbody className="divide-y divide-border/60">
                        {g.invoices.map((i) => (
                          <tr key={i.id} data-testid={`${testid}-row-${i.id}`}>
                            <td className="py-1.5 pr-2 whitespace-nowrap text-muted-foreground">{fmtDate(i.date)}</td>
                            <td className="py-1.5 px-2 font-mono font-medium">{i.reference}</td>
                            <td className="py-1.5 px-2 hidden sm:table-cell max-w-[220px] truncate text-muted-foreground">{i.work}{i.po ? ` · PO ${i.po}` : ""}</td>
                            <td className="py-1.5 px-2"><Pill map={INVOICE_STATUS} value={i.status} /></td>
                            <td className="py-1.5 pl-2 text-right font-[Chivo] font-semibold whitespace-nowrap">{fmtMoney(i.total)}{i.outstanding > 0 && i.outstanding !== i.total && <span className="block text-[10px] text-amber-600 font-normal">{fmtMoney(i.outstanding)} due</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">Statement preview · page 1</p>
          <button type="button" onClick={() => run("open")} className="block w-full rounded-md border border-border bg-white shadow-sm overflow-hidden aspect-[1/1.414] relative group text-left" title="Open the full PDF" data-testid={`${testid}-preview`}>
            {preview === "error" ? <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground text-xs gap-2"><FileText size={28} weight="duotone" /> Preview unavailable</div>
              : preview ? <img src={preview} alt={`Statement ${monthLabel(month)} preview`} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]" data-testid={`${testid}-preview-img`} />
              : <div className="absolute inset-0 bg-muted/40 animate-pulse" data-testid={`${testid}-preview-loading`} />}
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent text-white text-[11px] px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">Open full PDF →</span>
          </button>
        </div>
      </div>
    </Section>
  );
}
